import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

interface SavedFrameProps {
  /** Camera picture URL without query string, e.g. `${API_BASE}cameras/CAM-0-24/snapshot.jpg` */
  src: string;
  alt?: string;
  className?: string;
  /** No longer used (the view shows the saved picture); kept so callers need not change. */
  maxFps?: number;
  /** "tile": a small cell on a video wall: the small saved picture. */
  size?: 'full' | 'tile';
  /** Called when no picture could be loaded a few times in a row. The view keeps trying. */
  onError?: () => void;
}

/** How often a view checks for a newer saved picture; the server saves one every 10 minutes. */
const CHECK_MS = 60_000;

/** cameras/{id}/snapshot.jpg (or still.jpg) -> cameras/{id}/last-snapshot.jpg */
export const savedPictureUrl = (src: string, small = false) =>
  `${src.replace(/\/(snapshot|still)\.jpg$/, '/last-snapshot.jpg')}${small ? '?thumb=1' : ''}`;

/**
 * Camera view showing the camera's saved picture, for lists, walls and grids (13 Oct 2026). A view the operator
 * opens for one camera (popup, enlarged tile) uses LiveFrame (live video) instead.
 *
 * The server keeps one plain picture of every camera, taken every 10 minutes (backend cameras.py, the same
 * picture as the Cameras page). This view shows that picture and checks for a newer one once a minute; the
 * time it was taken is in the picture's tooltip. Before, every view loaded a new live picture up to 12 times
 * a second, and every such request counted as somebody watching: the analytics service then drew every frame
 * of the camera, in the same process that counts, and view-only cameras stayed open. With many views open the
 * Antharalayam door cameras fell to ~1 analysed frame per second.
 */
export const SavedFrame = forwardRef<HTMLImageElement, SavedFrameProps>(function SavedFrame(
  { src, alt, className, size = 'full', onError }, ref,
) {
  const imgRef = useRef<HTMLImageElement>(null);
  useImperativeHandle(ref, () => imgRef.current as HTMLImageElement);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastUrl = '';
    let lastStamp = '';
    let failures = 0;
    const ctrl = new AbortController();
    const url = savedPictureUrl(src, size === 'tile');

    const load = async () => {
      const one = new AbortController();                 // this request only: given up after 15 s
      const giveUp = setTimeout(() => one.abort(), 15000);
      const stopAll = () => one.abort();
      ctrl.signal.addEventListener('abort', stopAll);
      try {
        const r = await fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Math.floor(Date.now() / CHECK_MS)}`,
          { signal: one.signal, cache: 'no-store' });
        if (!r.ok) throw new Error(String(r.status));
        const stamp = r.headers.get('last-modified') || '';
        if (stamp && stamp === lastStamp) { failures = 0; return; }      // same picture as on screen
        const blob = await r.blob();
        if (!alive) return;
        const obj = URL.createObjectURL(blob);
        if (imgRef.current) {
          imgRef.current.src = obj;
          if (stamp) {
            const t = new Date(stamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
            imgRef.current.title = `Picture taken at ${t} (a new one every 10 minutes)`;
          }
        }
        if (lastUrl) URL.revokeObjectURL(lastUrl);
        lastUrl = obj;
        lastStamp = stamp;
        failures = 0;
      } catch {
        if (!alive) return;
        failures += 1;
        if (failures === 3) onErrorRef.current?.();
      } finally {
        clearTimeout(giveUp);
        ctrl.signal.removeEventListener('abort', stopAll);
      }
    };

    const tick = async () => {
      if (!alive) return;
      await load();
      if (alive) timer = setTimeout(tick, failures ? 15000 : CHECK_MS);
    };
    tick();
    return () => {
      alive = false;
      ctrl.abort();
      if (timer) clearTimeout(timer);
      if (lastUrl) URL.revokeObjectURL(lastUrl);
    };
  }, [src, size]);

  // an <img> with no picture yet shows the browser's broken-image icon and the alt text: start with an empty
  // 1x1 picture, which is replaced by the saved picture
  return <img ref={imgRef} alt={alt} className={className}
    src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" />;
});
